import {
  Alert,
  Box,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Switch
} from "@mui/material"
import { toast } from "react-toastify"

import {
  IntegrationEventsDocument,
  useIntegrationEventsQuery,
  useSetIntegrationEventMutation
} from "src/graphql/quark/quark.type"

const EVENT_LABELS: Record<string, { title: string; description: string }> = {
  "product.changed": {
    title: "Product updated",
    description:
      "Publish price and availability for mapped products. While off, none are sent and the integration keeps the last ones it received."
  },
  "stock.changed": {
    title: "Stock changed",
    description:
      "Publish stock levels and max per order for mapped products. While off, none are sent and the integration keeps the last ones it received."
  },
  "order.created": {
    title: "Order created",
    description: "Notify this integration when an order is placed."
  },
  "order.cancelled": {
    title: "Order cancelled",
    description: "Notify this integration when an order is cancelled."
  }
}

const EventsTab = ({
  connectionId,
  canEdit
}: {
  connectionId: string
  canEdit: boolean
}) => {
  const { data, loading } = useIntegrationEventsQuery({
    variables: { connectionId },
    fetchPolicy: "network-only"
  })

  const [setEvent, { loading: saving }] = useSetIntegrationEventMutation({
    refetchQueries: [
      { query: IntegrationEventsDocument, variables: { connectionId } }
    ]
  })

  const handleToggle = async (eventType: string, enabled: boolean) => {
    try {
      await setEvent({
        variables: { input: { connectionId, eventType, enabled } }
      })
      toast.success(
        `${EVENT_LABELS[eventType]?.title ?? eventType} ${enabled ? "enabled" : "disabled"}`
      )
    } catch (error: any) {
      toast.error(error?.message ?? "Could not change the event")
    }
  }

  if (loading)
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    )

  const events = data?.fusionIntegrationEvents ?? []

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        Events are recorded here, but are not yet delivered automatically —
        changes currently publish on a scheduled sync. Automatic delivery
        arrives once the platform emits these events.
      </Alert>

      <List>
        {events.map((event) => {
          const label = EVENT_LABELS[event.eventType]
          return (
            <ListItem
              key={event.eventType}
              divider
              secondaryAction={
                <Switch
                  edge="end"
                  checked={event.enabled}
                  disabled={!canEdit || saving}
                  onChange={(e) =>
                    void handleToggle(event.eventType, e.target.checked)
                  }
                />
              }
            >
              <ListItemText
                primary={label?.title ?? event.eventType}
                secondary={label?.description ?? event.eventType}
              />
            </ListItem>
          )
        })}
        {events.length === 0 && (
          <Box p={3}>This integration does not publish any events.</Box>
        )}
      </List>
    </Box>
  )
}

export default EventsTab
